import React, { useEffect, useMemo, useRef, useState } from "react";
import { AiPanelHeader } from "./AiPanelHeader";
import { AiPanelMessages } from "./AiPanelMessages";
import { AiPanelInput } from "./AiPanelInput";
import { BackgroundTasksBar } from "./BackgroundTasksBar";
import { SessionHistorySidebar } from "./SessionHistorySidebar";
import {
  useAiEvents,
  useAiHistory,
  useAiConnectionStatus,
  useLoadOlderHistory,
  useSendMessage,
  useResetSession,
  useInterruptAgent,
  useBackgroundTool,
  useOpenNewChat,
  useRestartSession,
  useAiQueue,
} from "../../hook/ai-panel";
import { useAiConnection } from "../../connection/AiConnectionProvider";
import { backgroundTasks, chatLifecycle } from "../../connection/chatPhase";
import { useActiveSessionId } from "../../slices/ui";
import { AiEventType, ConnectionStatus } from "../../types/ai-event";
import { ModelPicker } from "./ModelPicker";
import { ProviderPicker } from "./ProviderPicker";
import { DevcontainerPicker } from "./DevcontainerPicker";
import { providerRefusal } from "../../connection/providerRefusal";
import { ProviderRefusal } from "./ProviderRefusal";

interface ChatSurfaceProps {
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onClose?: () => void;
  // false in the Phone Shell: Chats is its own view there.
  historyRail?: boolean;
}

export const ChatSurface: React.FC<ChatSurfaceProps> = ({
  fullscreen,
  onToggleFullscreen,
  onClose,
  historyRail = true,
}) => {
  const [historyOpen, setHistoryOpen] = useState(!!fullscreen || !onClose);
  const events = useAiEvents();
  const history = useAiHistory();
  const activeSessionId = useActiveSessionId();
  const [connectionStatus] = useAiConnectionStatus();
  const sendMessage = useSendMessage();
  const { queued, enqueue, dequeue, cancel } = useAiQueue();
  const loadOlder = useLoadOlderHistory();
  const { sendQuestionReply, hadConnected, chatLoading } = useAiConnection();
  const resetSession = useResetSession();
  const interruptAgent = useInterruptAgent();
  const backgroundTool = useBackgroundTool();
  const handleNewChat = useOpenNewChat();
  const restartSession = useRestartSession();
  const [refusal, setRefusal] = useState<NonNullable<ReturnType<typeof providerRefusal>> | null>(null);
  const [dismissedCode, setDismissedCode] = useState<string | null>(null);
  const refusalCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const lastError = [...events].reverse().find((event) => event.type === AiEventType.SESSION_ERROR);
    const code = lastError && "code" in lastError ? lastError.code : undefined;
    const next = code ? providerRefusal(code) : null;
    // A dismissal covers this refusal; a different refusal code shows the banner again.
    if ((next?.code ?? null) !== refusalCodeRef.current) {
      refusalCodeRef.current = next?.code ?? null;
      setDismissedCode(null);
    }
    setRefusal(next);
  }, [events]);

  // One scan per events change (chatPhase.ts) plus the socket/loading inputs: header, footer and
  // composer all consume this single result (spec Decision 2).
  const connectionFailed =
    connectionStatus === ConnectionStatus.DISCONNECTED &&
    events[events.length - 1]?.type === AiEventType.CONNECTION_ERROR;
  const lifecycle = useMemo(
    () =>
      chatLifecycle({
        status: connectionStatus,
        failed: connectionFailed,
        hadConnected,
        loading: chatLoading,
        events,
        activeSessionId,
      }),
    [connectionStatus, connectionFailed, hadConnected, chatLoading, events, activeSessionId]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <AiPanelHeader
        connectionStatus={connectionStatus}
        connectionFailed={connectionFailed}
        lifecycle={lifecycle.phase}
        activeSessionId={activeSessionId}
        fullscreen={fullscreen}
        historyOpen={historyOpen}
        onToggleHistory={historyRail ? () => setHistoryOpen((o) => !o) : undefined}
        onToggleFullscreen={onToggleFullscreen}
        onClose={onClose}
        onClear={resetSession}
      />
      {refusal && refusal.code !== dismissedCode && (
        <ProviderRefusal refusal={refusal} onRestart={restartSession} onDismiss={() => setDismissedCode(refusal.code)} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", borderBottom: "1px solid rgba(128,128,128,0.2)", fontSize: 12, flexWrap: "wrap" }}>
        <span style={{ opacity: 0.6 }}>Provider:</span>
        <ProviderPicker />
        <span style={{ opacity: 0.6 }}>Model:</span>
        <ModelPicker />
        <span style={{ opacity: 0.6 }}>Devcontainer:</span>
        <DevcontainerPicker />
      </div>
      <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {historyRail && <SessionHistorySidebar open={historyOpen} onNewChat={handleNewChat} />}
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, overflow: "hidden" }}>
          {/* Approval requests are display-only: the agent service pre-allows every tool, so
              there is no approval reply frame for these handlers to send. */}
          <AiPanelMessages
            events={events}
            lifecycle={lifecycle.phase}
            activeSessionId={activeSessionId}
            isRunning={lifecycle.busy}
            history={history}
            onLoadOlder={loadOlder}
            onApprove={(_id) => undefined}
            onDeny={(_id) => undefined}
            onQuestionReply={sendQuestionReply}
            queued={queued}
            onCancelQueued={cancel}
            onBackground={backgroundTool}
          />
          {(lifecycle.phase === "idle" || lifecycle.phase === "running") && (
            <BackgroundTasksBar tasks={backgroundTasks(events)} onStop={interruptAgent} />
          )}
          <AiPanelInput
            onSend={sendMessage}
            onInterrupt={interruptAgent}
            connectionStatus={connectionStatus}
            isRunning={lifecycle.busy}
            hold={lifecycle.hold}
            disabled={connectionStatus !== ConnectionStatus.CONNECTED}
            queued={queued}
            onQueue={enqueue}
            onDequeue={dequeue}
            lastEventType={events[events.length - 1]?.type}
          />
        </div>
      </div>
    </div>
  );
};
